import React from 'react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { FolderPlus, Pencil, Trash2 } from 'lucide-react';
import { Permission } from '../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../utils/hooks/PermissionGate';
import { PartnerEntity } from '../../../../../utils/types/admin_types';

interface EntityContextMenuProps {
  entity: PartnerEntity;
  anchorEl: HTMLElement | null;
  anchorPosition?: { top: number; left: number } | null;
  open: boolean;
  onClose: () => void;
  onEditClick: (entity: PartnerEntity) => void;
  onDeleteClick: (entity: PartnerEntity) => void;
  onAddClick: (entity: PartnerEntity) => void;
}

export const EntityContextMenu: React.FC<EntityContextMenuProps> = ({
  entity,
  anchorEl,
  anchorPosition,
  open,
  onClose,
  onEditClick,
  onDeleteClick,
  onAddClick,
}) => {
  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorReference={anchorPosition ? 'anchorPosition' : 'anchorEl'}
      anchorPosition={anchorPosition || undefined}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      PaperProps={{
        elevation: 3,
        sx: {
          borderRadius: '10px',
          minWidth: 165,
          border: '1px solid #e2e8f0',
          boxShadow:
            '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
          p: 0.5,
        },
      }}
    >
      <PermissionGate requiredPermissions={[Permission.PARTNER_CREATE]}>
        <MenuItem
          dense
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            onAddClick(entity);
          }}
          sx={{
            borderRadius: '6px',
            fontSize: '0.8125rem',
            py: 0.65,
            px: 1.25,
            gap: 1.25,
            color: '#1e293b',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 20, color: '#10b981' }}>
            <FolderPlus size={15} />
          </ListItemIcon>
          <ListItemText
            primary="Add Sub-Entity"
            primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
          />
        </MenuItem>
      </PermissionGate>

      <PermissionGate requiredPermissions={[Permission.PARTNER_EDIT]}>
        <MenuItem
          dense
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            onEditClick(entity);
          }}
          sx={{
            borderRadius: '6px',
            fontSize: '0.8125rem',
            py: 0.65,
            px: 1.25,
            gap: 1.25,
            color: '#1e293b',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 20, color: '#0284c7' }}>
            <Pencil size={15} />
          </ListItemIcon>
          <ListItemText
            primary="Edit Entity"
            primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
          />
        </MenuItem>
      </PermissionGate>

      <PermissionGate requiredPermissions={[Permission.PARTNER_DELETE]}>
        <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />
        <MenuItem
          dense
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            onDeleteClick(entity);
          }}
          sx={{
            borderRadius: '6px',
            fontSize: '0.8125rem',
            py: 0.65,
            px: 1.25,
            gap: 1.25,
            color: '#ef4444',
            '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 20, color: '#ef4444' }}>
            <Trash2 size={15} />
          </ListItemIcon>
          <ListItemText
            primary="Delete Entity"
            primaryTypographyProps={{
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: '#ef4444',
            }}
          />
        </MenuItem>
      </PermissionGate>
    </Menu>
  );
};
